/**
 * Sagas / franquicias del catálogo.
 *
 * Prioridad:
 * 1) Reglas del admin (API /api/sagas/rules)
 * 2) SAGA_DEFS fijas en código
 * 3) Auto-detección por base del título (secuelas)
 */

export const SAGA_DEFS = [
  {
    slug: "james-bond",
    title: "James Bond",
    match: (t) =>
      /james\s*bond|\b007\b|goldfinger|desde rusia con amor|from russia with love|diamantes para la eternidad|diamonds are forever|s[oó]lo se vive dos veces|you only live twice|operaci[oó]n trueno|thunderball|dr\.?\s*no|agente\s*007|casino royale|skyfall|spectre|no time to die|sin tiempo para morir|quantum of solace|die another day|muere otro d[ií]a|the world is not enough|el mundo nunca es suficiente|tomorrow never dies|el ma[nñ]ana nunca muere|goldeneye|licence to kill|licencia para matar|the living daylights|a view to a kill|panorama para matar|octopussy|for your eyes only|s[oó]lo para sus ojos|moonraker|the spy who loved me|la esp[ií]a que me am[oó]|the man with the golden gun|el hombre de la pistola de oro|live and let die|vive y deja morir|on her majesty|al servicio secreto|nunca digas nunca jam[aá]s|never say never again/i.test(
        t
      ),
  },
  {
    slug: "john-wick",
    title: "John Wick",
    match: (t) => /john\s*wick/i.test(t),
  },
  {
    slug: "regreso-al-futuro",
    title: "Regreso al futuro",
    match: (t) =>
      /regreso al futuro|volver al futuro|back to the future/i.test(t),
  },
  {
    slug: "ataque-a-los-titanes",
    title: "Ataque a los Titanes",
    match: (t) => /ataque a los titanes|attack on titan/i.test(t),
  },
  {
    slug: "bad-boys",
    title: "Bad Boys",
    match: (t) => /bad\s*boys/i.test(t),
  },
  {
    slug: "mortal-kombat",
    title: "Mortal Kombat",
    match: (t) => /mortal\s*kombat/i.test(t),
  },
  {
    slug: "avatar",
    title: "Avatar",
    match: (t) =>
      /\bavatar\b/i.test(t) && !/aang|airbender|maestro del aire/i.test(t),
  },
  {
    slug: "soldado-universal",
    title: "Soldado universal",
    match: (t) => /soldado universal|universal soldier/i.test(t),
  },
  {
    slug: "ahora-me-ves",
    title: "Ahora me ves",
    match: (t) => /ahora me ves|now you see me/i.test(t),
  },
  {
    slug: "deadpool",
    title: "Deadpool",
    match: (t) =>
      /deadpool|wolverine.*deadpool|deadpool.*lobezno|deadpool.*wolverine/i.test(t),
  },
  {
    slug: "noche-de-bodas",
    title: "Noche de bodas",
    match: (t) => /noche de bodas|shotgun wedding/i.test(t),
  },
  {
    slug: "guardianes-de-la-noche",
    title: "Guardianes de la noche",
    match: (t) =>
      /guardianes de la noche|kimetsu|demon\s*slayer|asesino de demonios/i.test(t),
  },
  {
    slug: "toy-story",
    title: "Toy Story",
    match: (t) => /toy\s*story|historia de juguetes/i.test(t),
  },
  {
    slug: "studio-ghibli",
    title: "Studio Ghibli",
    match: (t) =>
      /viaje de chihiro|spirited away|princesa mononoke|mi vecino totoro|castillo ambulante|howl|el ni[nñ]o y la bestia|summer wars|origen.*esp[ií]ritus|ponyo|nausica|tumba de las luci[eé]rnagas|el viento se levanta|kiki|servicio de entregas/i.test(
        t
      ),
  },
];

const SEQUEL_SUFFIX_RE =
  /(?:[\s:·\-–—]*(?:parte|part|chapter|cap[ií]tulo|vol\.?|volume)\s*[0-9ivxlcdm]+|[\s:·\-–—]*\b(?:ii|iii|iv|v|vi|vii|viii|ix|x)\b|[\s:·\-–—]*\b[2-9]\b|\s*[.…]+$|\s*\([^)]*\)\s*$)+$/i;

export function normalizeTitle(title) {
  return String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Quita numeración de secuela para agrupar franquicias. */
export function franchiseBase(title) {
  let n = normalizeTitle(title);
  let prev = null;
  while (prev !== n) {
    prev = n;
    n = n.replace(SEQUEL_SUFFIX_RE, "").trim();
  }
  return n;
}

export function slugifySaga(text) {
  return (
    normalizeTitle(text)
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 64) || "saga"
  );
}

function titleCaseEs(base) {
  const small = new Set(["de", "del", "la", "las", "el", "los", "y", "a", "en", "al"]);
  const words = String(base || "").split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      if (i > 0 && small.has(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

function sortByYearThenTitle(a, b) {
  const ya = Number(a.year) || 0;
  const yb = Number(b.year) || 0;
  if (ya !== yb) return ya - yb;
  return String(a.title).localeCompare(String(b.title), "es");
}

function isMovieLike(v) {
  const t = (v?.content_type || "movie").toLowerCase();
  return t === "movie" || t === "anime";
}

function sagaPayload(slug, title, items, source, meta = {}) {
  const withCover = [...items].reverse().find((v) => v.cover_url) || items[0];
  return {
    slug,
    title,
    items,
    count: items.length,
    cover_url: withCover?.cover_url || null,
    backdrop_url: withCover?.backdrop_url || null,
    years: items.map((v) => v.year).filter(Boolean),
    source, // "custom" | "manual" | "auto"
    ...meta,
  };
}

/** ¿El vídeo encaja en una regla del admin? */
export function videoMatchesRule(video, rule) {
  if (!video || !rule) return false;
  const ids = Array.isArray(rule.video_ids) ? rule.video_ids.map(Number) : [];
  if (ids.includes(Number(video.id))) return true;
  const t = normalizeTitle(video.title);
  const patterns = Array.isArray(rule.patterns) ? rule.patterns : [];
  for (const raw of patterns) {
    const p = normalizeTitle(raw);
    if (p && t.includes(p)) return true;
  }
  return false;
}

/** Preview: películas que coinciden con title/patterns/ids. */
export function previewRuleMatches(videos, { patterns = [], video_ids = [] } = {}) {
  const list = (Array.isArray(videos) ? videos : []).filter(isMovieLike);
  const rule = { patterns, video_ids };
  return list.filter((v) => videoMatchesRule(v, rule)).sort(sortByYearThenTitle);
}

/**
 * @param {Array} videos
 * @param {{ minCount?: number, includeAuto?: boolean, customRules?: Array }} [opts]
 */
export function buildSagas(videos, opts = {}) {
  const minCount = opts.minCount ?? 2;
  const includeAuto = opts.includeAuto !== false;
  const customRules = (opts.customRules || []).filter((r) => r && r.enabled !== false);
  const list = (Array.isArray(videos) ? videos : []).filter(isMovieLike);
  const used = new Set();
  const sagas = [];
  const usedSlugs = new Set();

  // 1) Reglas del admin (prioridad)
  for (const rule of customRules) {
    const items = list
      .filter((v) => v && !used.has(v.id) && videoMatchesRule(v, rule))
      .sort(sortByYearThenTitle);
    if (items.length < minCount) continue;
    items.forEach((v) => used.add(v.id));
    let slug = rule.slug || slugifySaga(rule.title);
    if (usedSlugs.has(slug)) slug = `${slug}-${rule.id || "c"}`;
    usedSlugs.add(slug);
    sagas.push(
      sagaPayload(slug, rule.title || titleCaseEs(slug), items, "custom", {
        ruleId: rule.id || null,
        patterns: rule.patterns || [],
        video_ids: rule.video_ids || [],
      })
    );
  }

  // 2) Reglas fijas de código
  for (const def of SAGA_DEFS) {
    if (usedSlugs.has(def.slug)) continue;
    const items = list
      .filter((v) => {
        if (!v || used.has(v.id)) return false;
        return def.match(normalizeTitle(v.title));
      })
      .sort(sortByYearThenTitle);

    if (items.length < minCount) continue;
    items.forEach((v) => used.add(v.id));
    usedSlugs.add(def.slug);
    sagas.push(sagaPayload(def.slug, def.title, items, "manual"));
  }

  // 3) Auto-detección
  if (includeAuto) {
    const buckets = new Map();
    for (const v of list) {
      if (!v || used.has(v.id)) continue;
      const key = franchiseBase(v.title);
      if (!key || key.length < 3) continue;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(v);
    }

    for (const [key, raw] of buckets) {
      const items = [...raw].sort(sortByYearThenTitle);
      if (items.length < minCount) continue;
      if (key.split(/\s+/).length === 1 && key.length < 5) continue;

      let slug = slugifySaga(key);
      if (usedSlugs.has(slug)) slug = `${slug}-auto`;
      usedSlugs.add(slug);
      items.forEach((v) => used.add(v.id));
      sagas.push(
        sagaPayload(slug, titleCaseEs(key), items, "auto", {
          suggestPatterns: [key],
        })
      );
    }
  }

  return sagas.sort(
    (a, b) => b.count - a.count || a.title.localeCompare(b.title, "es")
  );
}

export function findSaga(videos, slug, opts = {}) {
  return (
    buildSagas(videos, { ...opts, minCount: 1 }).find((s) => s.slug === slug) || null
  );
}

/**
 * Informe para admin.
 * @param {Array} videos
 * @param {Array} [customRules]
 */
export function sagaInsights(videos, customRules = []) {
  const list = (Array.isArray(videos) ? videos : []).filter(isMovieLike);
  const all = buildSagas(list, { minCount: 2, includeAuto: true, customRules });
  const custom = all.filter((s) => s.source === "custom");
  const active = all.filter((s) => s.source === "manual" || s.source === "custom");
  const discovered = all.filter((s) => s.source === "auto");

  const incomplete = [];
  for (const def of SAGA_DEFS) {
    const items = list.filter((v) => def.match(normalizeTitle(v.title)));
    if (items.length === 1) {
      incomplete.push({
        slug: def.slug,
        title: def.title,
        count: 1,
        items,
        hint: "Solo 1 película: hace falta otra para mostrar la saga.",
      });
    }
  }
  for (const rule of customRules || []) {
    if (rule.enabled === false) continue;
    const items = list.filter((v) => videoMatchesRule(v, rule));
    if (items.length === 1) {
      incomplete.push({
        slug: rule.slug,
        title: rule.title,
        count: 1,
        items,
        ruleId: rule.id,
        hint: "Regla con solo 1 coincidencia: añade otra película o más palabras clave.",
      });
    }
  }

  return {
    all,
    custom,
    active,
    discovered,
    incomplete,
    newCount: discovered.length,
    activeCount: active.length,
    customCount: custom.length,
  };
}

/** Sugiere patrones a partir de títulos de una saga auto. */
export function suggestPatternsFromTitles(titles) {
  const bases = [...new Set((titles || []).map((t) => franchiseBase(t)).filter(Boolean))];
  return bases.length ? bases : [(titles || [])[0] ? normalizeTitle(titles[0]) : ""].filter(Boolean);
}
